import React, {
  ChangeEvent,
  DragEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { ReactComponent as Gallery } from "../../assets/gallary.svg";
import Button from "../Button";
import Typography from "../Typography";

interface MediaUploaderProps {
  label?: string;
  image?: string[]; // You can still pass an array of strings as initial images
  style?: {
    container?: React.CSSProperties;
    label?: React.CSSProperties;
    input?: React.CSSProperties;
    dropzone?: React.CSSProperties;
    imagePreview?: React.CSSProperties;
    crossButton?: React.CSSProperties;
  };
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onDrop?: (e: DragEvent<HTMLDivElement>) => void;
  acceptedFileTypes?: string[]; // Default types: image/*, video/*
  multiple?: boolean; // Whether multiple files can be uploaded
  setSelectedFiles?: any;
  onRemove?: (index: number) => void;
}

interface ImagePreview {
  file: File; // The actual file object
  previewUrl: string; // Blob URL for the preview
}

const MediaUploader: React.FC<MediaUploaderProps> = ({
  label,
  image = [],
  style,
  onChange,
  onDrop,
  acceptedFileTypes = ["image/*", "video/*"],
  setSelectedFiles,
  multiple = false,
  onRemove,
}) => {
  const [imagePreviews, setImagePreviews] = useState<ImagePreview[]>([]); // State to hold image previews
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (image && image.length > 0) {
      // Assuming the initial `image` prop contains URLs, not file objects
      setImagePreviews(
        image.map((url) => ({
          file: new File([], ""), // Placeholder for file, we won't use it
          previewUrl: url, // Use the URL as the preview
        }))
      );
    }
  }, [image]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      const newPreviews = files.map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file), // Store the preview URL for display
      }));

      setImagePreviews((prev) => [...prev, ...newPreviews]);

      if (onChange) {
        onChange(e);
      }
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const fileInputEvent = {
        target: { files },
      } as ChangeEvent<HTMLInputElement>;
      handleFileChange(fileInputEvent);
    }
    if (onDrop) {
      onDrop(e);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const triggerFileInput = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const removeImage = (index: number) => {
    onRemove?.(index);
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div style={style?.container}>
      {label && <Typography style={style?.label}>{label}</Typography>}
      {imagePreviews.length > 0 ? (
        <>
          <div
            style={{
              ...style?.imagePreview,
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 10,
              padding: 20,
              position: "relative",
            }}
          >
            {imagePreviews.map(({ previewUrl }, index) => (
              <div
                key={index}
                style={{
                  position: "relative",
                  width: "150px",
                  height: "150px",
                  backgroundColor: "#F9F9FD",
                  border: "1px dashed #E3DFF6",
                  padding: 5,
                }}
              >
                {/* Cross button */}
                <button
                  onClick={() => {
                    removeImage(index);
                    const updatedImages = image.filter((_, i) => i !== index); // Creates a new array without the image at the index
                    if (setSelectedFiles) {
                      setSelectedFiles(updatedImages); // Update state with the new array
                    }
                  }}
                  style={{
                    position: "absolute",
                    width: "24px",
                    height: "24px",
                    top: 3,
                    right: 3,
                    backgroundColor: "#D5CFF2",
                    border: "3px solid #E3DFF6 ",
                    fontSize: 12,
                    fontWeight: "100",
                    color: "#806ED8", // Red color for the cross
                    cursor: "pointer",
                    borderRadius: 40,
                  }}
                >
                  ✖
                </button>

                <img
                  src={previewUrl}
                  alt={`Preview ${index}`}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
            ))}
          </div>
        </>
      ) : (
        <div
          style={{
            ...style?.dropzone,
            display: "flex",
            flexDirection: "column",
            border: "2px dashed #E3DFF6",
            padding: "20px",
            cursor: "pointer",
            gap: 12,
            alignItems: "center",
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <div
            style={{
              borderWidth: 4,
              borderRadius: 35,
              padding: 6,
              backgroundColor: "#D5CFF2",
              borderColor: "#E3DFF6",
              borderStyle: "solid",
              width: 40,
            }}
          >
            <Gallery fill="#806ED8" />
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFileTypes.join(", ")}
            onChange={handleFileChange}
            style={style?.input}
            hidden
            multiple={multiple} // Enable multiple file selection
          />

          <span style={{ color: "#9EA1B1" }}>
            Click or Drag & Drop to upload files
          </span>

          <Button
            style={{ color: "#806ED8", background: "#E3DFF6", borderRadius: 8 }}
            onClick={triggerFileInput}
          >
            Add Files
          </Button>
        </div>
      )}
    </div>
  );
};

export default MediaUploader;
